/**
 * Staff importer (`staff` collection). The file REPLACES the list: staff
 * missing from the file are deleted, as the legacy Staff Management "Save"
 * did. The plan reads the whole `staff` collection from the server (the
 * legacy sync did the same; the collection is a few hundred docs at most and
 * removals cannot be found any other way) and writes only the docs that
 * changed.
 *
 * Two file formats:
 * - the Staff Management Portal sheet (xlsx / xls / csv, positional columns);
 * - the MOHRE "List of Employees / كشف العاملين بالمنشأة" PDF. Its records are
 *   matched to existing docs by passport number, else card number, a passport
 *   with one typo or the name (staffRows.matchStaffDoc; anything but the exact
 *   passport is listed for review) and keep their staff number; a new employee
 *   gets the card number as staff number. Legacy docs whose columns were
 *   shifted (name = passport ...) are read in their real meaning and written
 *   back straight. Everyone missing from the list is proposed for removal
 *   (the user confirms in the card) - but only when the PDF is complete: a
 *   printed total that differs from the parsed count, a missing total or a
 *   record without passport SKIPS the removals (they are listed as "Not in
 *   file (kept)"), as the hint promises. A PDF parsed at 80 of 120 employees
 *   must not delete 40 staff (audit ADM-06).
 *
 * With the new store (USE_CONTRACTS_V2) the staff are the engine's office records: the plan reads them with
 * GET /api/office/staff, the apply sends every write and delete in ONE call (POST /api/office/staff/import, one
 * transaction, logged by the engine) and gets back each touched document as it was before. That `before` is the
 * undo (undoLastStaffImport: the documents put back whole, the ones the import created deleted) - no Firestore
 * import_undo snapshot is written: the import card offers "Undo last import" through `storeUndo` (importRunner's
 * undoKindOf / runStoreUndo), for the last import made in this browser session.
 * The PDF / sheet is still parsed and planned in the browser.
 */
import { collection, doc, getDocsFromServer, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { PERMISSIONS } from '../../constants/permissions';
import { processStaffRows, diffStaff, staffFromMohre, staffRemovalsOf } from './staffRows';
import { isMohreEmployeeList, parseMohreEmployeeList } from './mohreList';
import { readWorkbook, firstSheetRows, samplesOf, sleep, BATCH_SIZE, BATCH_DELAY_MS } from './shared';
import { snapshotItemsFor, snapshotItemsFromMap } from '../importUndoService';
import { USE_CONTRACTS_V2 } from '../contractsV2';
import { finesService, officeError } from '../finesService';

const STAFF = 'staff';

// The undo of the last staff import of this browser session on the store: {at, source, before: {id: doc | null},
// after: {id: _version | null} (the version each touched document had right after the import; null = none)}.
// Kept in memory only (it holds passports and card numbers - never written to the browser's storage).
let lastStoreImport = null;

/** The store's staff documents: [{id, ...fields, _version}] (a failed read throws). */
const readStoreStaff = () => finesService.officeStaff().catch((e) => { throw officeError(e); });

/** {id: _version} of the given ids in a read of the store (null: no such document). */
const versionsOf = (items, ids) => {
    const byId = new Map((items || []).map((d) => [String(d.id), d._version ?? null]));
    return Object.fromEntries(ids.map((id) => [id, byId.has(id) ? byId.get(id) : null]));
};

/**
 * The request that takes an import back (pure, tested alone). `before`: each touched document as it was before
 * the import (null: the import created it); `after` / `current`: {id: _version | null} right after the import and
 * now. Only a document still exactly as the import left it goes back (a later edit is kept and listed in
 * `skipped`, as the Firestore undo did - ADM-04): one that existed is written back whole (merge false), one the
 * import created is deleted.
 */
export const staffUndoRequest = (before, after = null, current = null) => {
    const writes = [];
    const deletes = [];
    const skipped = [];
    Object.entries(before || {}).forEach(([id, data]) => {
        if (after && current && (after[id] ?? null) !== (current[id] ?? null)) { skipped.push(id); return; }
        if (data === null || data === undefined) {
            if (!current || (current[id] ?? null) !== null) deletes.push(id);   // already gone: nothing to delete
        } else {
            writes.push({ id, data, merge: false });
        }
    });
    return { writes, deletes, skipped, source: 'undo' };
};

/** What the last staff import of this session would undo: {at, source, restores, deletes}, or null. */
export const lastStaffImportUndo = () => {
    if (!lastStoreImport) return null;
    const { writes, deletes } = staffUndoRequest(lastStoreImport.before);
    return { at: lastStoreImport.at, source: lastStoreImport.source, restores: writes.length, deletes: deletes.length };
};

/**
 * Takes the last staff import of this session back on the store (one transaction). Returns
 * {success, restored, deleted, skipped: [ids changed since the import, kept]} or {success: false, error}.
 */
export const undoLastStaffImport = async (onProgress) => {
    if (!USE_CONTRACTS_V2) return { success: false, error: 'The staff import is undone from the import card.' };
    if (!lastStoreImport) return { success: false, error: 'No staff import of this session to undo.' };
    if (!lastStoreImport.after) {
        return { success: false, error: 'The staff as the import left them could not be read, so the import cannot be undone safely.' };
    }
    try {
        if (onProgress) onProgress({ done: 0, total: 1, label: 'Checking which staff records changed since the import...' });
        const ids = Object.keys(lastStoreImport.before || {});
        const current = versionsOf(await readStoreStaff(), ids);
        const { writes, deletes, skipped, source } = staffUndoRequest(lastStoreImport.before, lastStoreImport.after, current);
        const total = writes.length + deletes.length;
        if (total > 0) {
            if (onProgress) onProgress({ done: 0, total, label: `Restoring ${total} staff record(s)...` });
            await finesService.officeStaffImport({ writes, deletes, source }).catch((e) => { throw officeError(e); });
        }
        lastStoreImport = null;
        if (onProgress) onProgress({ done: total, total: total || 1, label: 'Done' });
        return { success: true, restored: writes.length, deleted: deletes.length, skipped };
    } catch (error) {
        return { success: false, error: error?.message || String(error) };
    }
};

const docSample = (d) => ({ staff_no: d.write.staff_no, name: d.write.name ?? '', job: d.write.job ?? '', nationality: d.write.nationality ?? '', card_expiry: d.write.card_expiry ?? '' });
const removeSample = (r) => ({ doc_id: r.id, staff_no: r.staff_no, name: r.name, reason: r.shadow ? 'duplicate id of a kept staff no.' : 'not in file' });

const isPdf = (file) => /\.pdf$/i.test(file?.name || '') || file?.type === 'application/pdf';

/** MOHRE PDF -> { employees (parser records), pdf: { total, warnings } }; the plan maps them to staff rows once the collection is read. */
const parseMohrePdf = async (file) => {
    const { pdfTextLines } = await import('./pdfText');
    const pages = await pdfTextLines(file);
    const lines = pages.flat();
    if (!isMohreEmployeeList(lines)) throw new Error('This PDF is not the MOHRE "List of Employees" (كشف العاملين بالمنشأة).');
    const parsed = parseMohreEmployeeList(lines);
    if (parsed.employees.length === 0) throw new Error('No employees found in the PDF.');
    return { employees: parsed.employees, pdf: { total: parsed.total, warnings: parsed.warnings } };
};

export const staffImporter = {
    id: 'staff',
    title: 'Staff',
    table: 'staff',
    permission: PERMISSIONS.import_staff,
    description: 'Replaces the staff list with the file: new staff numbers are added, changed records updated, and every staff record that is NOT in the file is deleted. Accepts the Staff Management sheet or the MOHRE "List of Employees" PDF.',
    hint: 'Sheet: first sheet, header in row 1, then columns in this order: Staff No, Name, Job, Nationality, Passport No, Card Type, Card Number, Card Expiry, Passport Issue Date, Passport Expiry, Email, Joining Date. Doc id = Staff No. MOHRE PDF: employees are matched to existing staff by passport number and keep their staff number; new employees get their card number as staff number; email, joining date and passport dates are kept from the database. Removals are skipped unless the PDF total matches the parsed count.',
    modes: [{ id: 'replace', label: 'Replace list (removes staff missing from the file)' }],
    accept: '.xlsx,.xls,.csv,.pdf',
    countLabels: { add: 'Add', update: 'Update', skip: 'Unchanged', remove: 'REMOVE (not in file)', invalid: 'No staff no.', noStaffNo: 'New (no staff no.)', notFound: 'Not in file (kept)' },

    async parse(file) {
        if (isPdf(file)) return parseMohrePdf(file);
        const workbook = await readWorkbook(file);
        const rows = firstSheetRows(workbook, { header: 1, defval: '' });
        const employees = processStaffRows(rows);
        if (employees.length === 0) throw new Error('No staff rows found (row 1 must be the header).');
        return employees;
    },

    async plan(input) {
        let existing;
        let existingById;
        if (USE_CONTRACTS_V2) {
            // the store's documents ({id, ...fields}; the version is not a field of the employee)
            existing = (await readStoreStaff()).map(({ _version, ...docData }) => docData);
            existingById = new Map(existing.map(({ id, ...docData }) => [id, docData]));
        } else {
            const snap = await getDocsFromServer(collection(db, STAFF));
            existing = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            // Raw stored data per doc id, kept for the undo snapshot (the whole collection was just read)
            existingById = new Map(snap.docs.map(d => [d.id, d.data()]));
        }
        const pdf = Array.isArray(input) ? null : input.pdf;
        const employees = pdf ? staffFromMohre(input.employees, existing) : input;
        const diff = diffStaff(employees, existing);

        // The MOHRE list is the authoritative head count: anyone missing from it is
        // proposed for removal (the user still confirms) - unless the PDF itself is not
        // complete (printed total != parsed count, no total, a record without passport):
        // then nobody is removed and the missing staff are only listed (as the hint says).
        const { removes, kept, blockReason } = staffRemovalsOf(pdf, employees, diff.removes);
        const counts = { add: diff.adds.length, update: diff.updates.length, skip: diff.unchanged.length, remove: removes.length, invalid: diff.invalid };
        const samples = {
            add: samplesOf(diff.adds, docSample),
            update: samplesOf(diff.updates, d => ({ ...docSample(d), changed: d.changed.join(', ') })),
            remove: samplesOf(removes, removeSample),
            skip: samplesOf(diff.unchanged, id => ({ staff_no: id }))
        };
        if (kept.length > 0) {
            counts.notFound = kept.length;
            samples.notFound = samplesOf(kept, r => ({ ...removeSample(r), reason: 'not in the PDF (kept: removals skipped)' }));
        }
        // The staff importer keeps an undo snapshot, so the deletion can be taken back
        // with "Undo last import" (the old text said it could not).
        let warning = removes.length > 0
            ? `${removes.length.toLocaleString()} staff record(s) in the database are not in this file and will be DELETED on apply (${existing.length.toLocaleString()} in the database now). Check the file is the complete list${USE_CONTRACTS_V2 ? ' ("Undo last import" can bring them back until this page is reloaded).' : ' ("Undo last import" can bring them back).'}`
            : null;

        let review = [];
        if (pdf) {
            const unmatched = new Set(employees.filter(e => !e.matched).map(e => e.staffNo));
            const noStaffNo = diff.adds.filter(a => unmatched.has(a.id));
            counts.noStaffNo = noStaffNo.length;
            samples.noStaffNo = samplesOf(noStaffNo, docSample);
            if (blockReason) {
                warning = `Check the PDF before applying: ${blockReason}.${kept.length > 0 ? ` Removals are skipped: ${kept.length.toLocaleString()} staff record(s) not in the PDF are kept.` : ''}`;
            }
            review = employees.filter(e => e.needsReview).map(e => ({
                label: [e.staffNo ? `#${e.staffNo}` : '', e.name, e.passportNumber].filter(Boolean).join(' - ') || '(unnamed record)',
                detail: (e.issues || []).join('; ')
            }));
        }

        return {
            table: STAFF,
            mode: 'replace',
            source: pdf ? 'mohre-pdf' : 'sheet',
            total: employees.length,
            counts,
            samples,
            warning,
            review,
            notes: pdf ? pdf.warnings : [],
            writes: [...diff.adds, ...diff.updates].map(d => ({ id: d.id, data: d.write })),
            deletes: removes.map(r => r.id),
            existingById,
            read: existing.length
        };
    },

    // Firestore only: the store's import answers its own undo (`before`, see undoLastStaffImport) and a Firestore
    // snapshot of the store's staff would restore nothing.
    ...(USE_CONTRACTS_V2 ? {} : {
        /**
         * Undo snapshot: the current document of every staff id the apply writes or
         * deletes. The plan already read the whole collection, so no document is
         * read twice; ids that are new get `data: null` (the undo deletes them).
         */
        async snapshot(plan) {
            const ids = [...(plan.writes || []).map(w => w.id), ...(plan.deletes || [])];
            if (plan.existingById instanceof Map) return snapshotItemsFromMap(STAFF, ids, plan.existingById);
            return snapshotItemsFor(STAFF, ids);
        },
    }),
    // the store's undo of the last import of this session: last() -> {at, source, restores, deletes} | null,
    // run(onProgress) -> {success, restored, deleted, skipped} (the import card offers it: importRunner.undoKindOf)
    ...(USE_CONTRACTS_V2 ? { storeUndo: { last: lastStaffImportUndo, run: undoLastStaffImport } } : {}),

    async apply(plan, _opts, onProgress) {
        const total = plan.writes.length + plan.deletes.length;
        let done = 0;
        const report = (label) => { if (onProgress) onProgress({ done, total, label }); };
        if (USE_CONTRACTS_V2) {
            // every write and delete in one transaction on the server: all of the file or nothing
            report(`Saving ${total.toLocaleString()} staff change(s)`);
            const answer = await finesService.officeStaffImport({
                writes: plan.writes.map(w => ({ id: w.id, data: w.data })),
                deletes: plan.deletes,
                source: plan.source || 'file'
            }).catch((e) => { throw officeError(e); });
            const before = answer?.before || {};
            // the version each touched document has now: the undo only takes back what nobody changed since
            let after = null;
            try {
                after = versionsOf(await readStoreStaff(), Object.keys(before));
            } catch {
                after = null;
            }
            lastStoreImport = { at: new Date().toISOString(), source: plan.source || 'file', before, after };
            done = total;
            report('Done');
            return { success: true, stats: { ...plan.counts } };
        }
        const stamp = new Date().toISOString();
        for (let i = 0; i < plan.writes.length; i += BATCH_SIZE) {
            if (i > 0) await sleep(BATCH_DELAY_MS);
            const batch = writeBatch(db);
            plan.writes.slice(i, i + BATCH_SIZE).forEach(w => batch.set(doc(db, STAFF, w.id), { ...w.data, last_updated: stamp }, { merge: true }));
            report(`Writing staff ${i + 1}-${Math.min(i + BATCH_SIZE, plan.writes.length)} of ${plan.writes.length}`);
            await batch.commit();
            done = Math.min(i + BATCH_SIZE, plan.writes.length);
        }
        for (let i = 0; i < plan.deletes.length; i += BATCH_SIZE) {
            if (i > 0 || plan.writes.length > 0) await sleep(BATCH_DELAY_MS);
            const batch = writeBatch(db);
            plan.deletes.slice(i, i + BATCH_SIZE).forEach(id => batch.delete(doc(db, STAFF, id)));
            report(`Deleting staff ${i + 1}-${Math.min(i + BATCH_SIZE, plan.deletes.length)} of ${plan.deletes.length}`);
            await batch.commit();
            done = plan.writes.length + Math.min(i + BATCH_SIZE, plan.deletes.length);
        }
        report('Done');
        return { success: true, stats: { ...plan.counts } };
    }
};

export default staffImporter;
